import React, { useEffect, useState } from 'react';
import FormGroup from '../Form/FormGroup';
import { Button } from '../Button';
import Checkbox from '../Form/Checkbox';
import { PasswordValidation } from '@nerdcoresdk/nerd-core-ui';
import { getIsPasswordValid } from '~src/utils/password-validation';
import Tooltip from '../Tooltip';
import { useRouter } from 'next/router';
import { useAuth } from '~src/hooks/useAuth';
import { getSignupDetailsForNonprofit } from '~src/resources/give-api.resource';

function initialFormElem() {
  return {
    value: '',
    error: false,
    message: '',
  };
}

const initialFormState = {
  firstName: initialFormElem(),
  lastName: initialFormElem(),
  email: initialFormElem(),
  password: initialFormElem(),
  confirmPassword: initialFormElem(),
  termsAndPolicy: initialFormElem(),
  atleast18: initialFormElem(),
};

export default function SignupForm({ onSubmit }) {
  const router = useRouter();
  const {fetcher} = useAuth();
  const [formState, setFormState] = useState(initialFormState);
  const [canSubmit, setCanSubmit] = useState(false);

  const [passwordPopoverOpen, setPasswordPopoverOpen] = useState(false);

  useEffect(() => {
    if (router.query.linkAccount && router.query.i && router.query.t) {
      fetcher(getSignupDetailsForNonprofit(router.query.i, router.query.t)).then(data => {
        setFormState(prev => {
          return {
            ...prev,
            firstName: {
              ...initialFormElem(),
              value: data.firstName
            },
            lastName: {
              ...initialFormElem(),
              value: data.lastName
            },
            email: {
              ...initialFormElem(),
              value: data.email
            }
          }
        })
      }, err => {
        console.error("Failed to load signup-details due to error: ", err);
      }).catch(ex => {
        console.error("Failed to load signup-details due to exception: ", ex);
      })
    }
  }, [fetcher, router.query.linkAccount, router.query.i, router.query.t]);

  function handleInputChange(e) {
    console.log('Handling change...');
    let value = e.target.value;
    if (e.target.type === 'checkbox') {
      value = !!e.target.checked;
    }

    let newFormState = {
      ...formState,
      [e.target.name]: {
        value,
        error: false,
        message: '',
      },
    };

    setFormState(newFormState);
    formIsValid(newFormState);
  }

  function handleInputBlur(e) {
    formIsValid(formState);
    if (e.target.name === 'password') {
      setPasswordPopoverOpen(false);
    }
  }

  function handlePasswordFocus(e) {
    setPasswordPopoverOpen(true);
  }

  const formIsValid = (tmpFormState) => {
    let valid = true;

    if (
      !tmpFormState.firstName.value ||
      !tmpFormState.lastName.value ||
      !tmpFormState.email.value
    ) {
      valid = false;
    }

    if (!tmpFormState.termsAndPolicy.value) {
      valid = false;
    }

    if (!tmpFormState.atleast18.value) {
      valid = false;
    }

    if (!getIsPasswordValid({ password: tmpFormState.password.value })) {
      valid = false;
    }

    if (tmpFormState.password.value !== tmpFormState.confirmPassword.value) {
      valid = false;
      tmpFormState.confirmPassword.error = true;
      tmpFormState.confirmPassword.message = 'Password values must match.';
    }

    setCanSubmit(valid);

    return valid;
  };

  function handleSubmit(e) {
    e.preventDefault();
    if (formIsValid(formState)) {
      console.log('Form is valid');
      onSubmit({
        fullName: null,
        firstName: formState.firstName.value,
        lastName: formState.lastName.value,
        email: formState.email.value,
        password: formState.password.value,
        newsletter: false,
      });
    } else {
      console.warn('Form is not valid.');
    }
  }

  React.useEffect(() => {
    formIsValid(formState);
  }, []);

  return (
    <div className="w-full flex flex-col">
      <div className="mb-7">
        <h1 className="text-3xl font-bold">Create your free Lemonaide account</h1>
        <p className="mt-3 text-giveGray-500">
          Already have an account?
          <Button as="a" variant="link" className="-ml-2" href="/login">
            Login
          </Button>
        </p>
      </div>

      <form onSubmit={handleSubmit} id="signup-form">
        <div className="flex flex-col lg:flex-row lg:gap-6">
          <div className="flex-1">
            <FormGroup
              label="First Name"
              value={formState.firstName.value}
              name="firstName"
              id="firstName"
              onChange={handleInputChange}
              inputProps={{
                placeholder: 'First Name',
              }}
            />
          </div>
          <div className="flex-1 mt-3 lg:mt-0">
            <FormGroup
              label="Last Name"
              value={formState.lastName.value}
              name="lastName"
              id="lastName"
              onChange={handleInputChange}
              inputProps={{
                placeholder: 'Last Name',
              }}
            />
          </div>
        </div>
        <FormGroup
          className="mt-3"
          type="email"
          label="Email Address"
          value={formState.email.value}
          name="email"
          id="email"
          onChange={handleInputChange}
          inputProps={{
            placeholder: 'Email Address',
          }}
        />
        <Tooltip
          isOpen={passwordPopoverOpen}
          positions={['right', 'bottom']}
          content={
            <div className="bg-black text-white px-2 py-1 rounded-md shadow-2xl">
              <PasswordValidation password={formState.password.value} className="" />
            </div>
          }
        >
          <div>
            <FormGroup
              className="mt-3 relative"
              type="password"
              label="Password"
              value={formState.password.value}
              name="password"
              id="password"
              onChange={handleInputChange}
              inputProps={{
                placeholder: 'Password',
                onBlur: handleInputBlur,
                onFocus: handlePasswordFocus,
              }}
              errorMessage={formState.password.error ? formState.password.message : null}
            />
          </div>
        </Tooltip>
        <FormGroup
          className="mt-3 mb-5"
          type="password"
          label="Confirm Password"
          value={formState.confirmPassword.value}
          name="confirmPassword"
          id="confirmPassword"
          onChange={handleInputChange}
          inputProps={{
            placeholder: 'Confirm Password',
            onBlur: handleInputBlur,
          }}
          errorMessage={
            formState.confirmPassword.error ? formState.confirmPassword.message : null
          }
        />
        <Checkbox
          inputProps={{
            name: 'termsAndPolicy',
            value: '1',
          }}
          onChange={handleInputChange}
          label={
            <span className="text-xs">
              By signing up you agree to Lemonaide&apos;s{' '}
              <a
                className="text-mint-600"
                href="https://www.lemonaide.co/legal#terms-of-use"
              >
                Terms of Use
              </a>{' '}
              and{' '}
              <a
                className="text-mint-600"
                href="https://www.lemonaide.co/legal#privacy-policy"
              >
                Privacy Policy
              </a>
            </span>
          }
        />
        <Checkbox
          inputProps={{
            name: 'atleast18',
            value: '1',
          }}
          onChange={handleInputChange}
          label={<span className="text-xs">I am 18+ years of age</span>}
        />

        <Button type="submit" className="mt-7" disabled={!canSubmit}>
          Create account
        </Button>
      </form>
    </div>
  );
}
